/** @format */

import React, { useState, useRef, useEffect } from "react";

const styles = {
  container: {
    position: "relative",
    width: "100%",
  },
  button: {
    width: "100%",
    padding: "0.375rem 0.75rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    color: "#212529",
    backgroundColor: "#fff",
    border: "1px solid #ced4da",
    borderRadius: "0.25rem",
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "calc(1.5em + 1rem + 2px)",
  },
  buttonText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    color: "#212529",
  },
  placeholder: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    color: "#6c757d",
  },
  chevron: {
    marginLeft: "8px",
    fontSize: "0.75rem",
    color: "#6c757d",
  },
  menu: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 1050,
    backgroundColor: "#fff",
    border: "1px solid #dee2e6",
    borderRadius: "0.25rem",
    maxHeight: "250px",
    overflowY: "auto",
    marginTop: "2px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    padding: "4px 0",
  },
  item: {
    padding: "0.5rem 0.75rem",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "0.875rem",
    color: "#212529",
    transition: "background-color 0.1s ease",
  },
  itemSelected: {
    backgroundColor: "#e8f4fd",
  },
  itemHover: {
    backgroundColor: "#f8f9fa",
  },
  itemSelectedHover: {
    backgroundColor: "#dceefb",
  },
  checkmark: {
    fontSize: "1rem",
    color: "#333",
    fontWeight: 600,
  },
};

function MultiSelectDropdown({
  options,
  value = [],
  onChange,
  placeholder = "Select...",
  required,
  name,
  selectRef,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    const newValue = value.includes(option)
      ? value.filter((v) => v !== option)
      : [...value, option];
    onChange(newValue);
  };

  const displayText = value.length > 0 ? value.join(", ") : null;

  return (
    <div style={styles.container} ref={containerRef}>
      {/* Hidden select for form validation */}
      <select
        ref={selectRef}
        name={name}
        multiple
        required={required}
        value={value}
        onChange={() => {}}
        style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      >
        {options.map((opt, i) => (
          <option key={i} value={opt}>{opt}</option>
        ))}
      </select>

      <button
        type="button"
        style={styles.button}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {displayText ? (
          <span style={styles.buttonText}>{displayText}</span>
        ) : (
          <span style={styles.placeholder}>{placeholder}</span>
        )}
        <span style={styles.chevron}>&#9662;</span>
      </button>

      {open && (
        <div style={styles.menu} role="listbox" aria-multiselectable="true">
          {options.map((option, i) => {
            const isSelected = value.includes(option);
            const isHovered = hoveredIndex === i;
            let itemStyle = { ...styles.item };
            if (isSelected && isHovered) {
              itemStyle = { ...itemStyle, ...styles.itemSelectedHover };
            } else if (isSelected) {
              itemStyle = { ...itemStyle, ...styles.itemSelected };
            } else if (isHovered) {
              itemStyle = { ...itemStyle, ...styles.itemHover };
            }

            return (
              <div
                key={i}
                role="option"
                aria-selected={isSelected}
                style={itemStyle}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(-1)}
                onClick={() => toggleOption(option)}
              >
                <span>{option}</span>
                {isSelected && <span style={styles.checkmark}>&#10003;</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;